// Reader custom links — the rules for a link, in one place.
//
// The editor, the public display and the admin remove path all need to agree
// about what a valid link is and what its URL looks like once stored. Keeping
// that here rather than inline in ProfileBlocks.jsx is the difference between
// one rule and three copies of it that drift.
//
// Storage shape, inside the block's own `data` in user_profiles.profile_blocks:
//
//   { links: [ { id, label, url, image_url }, ... ], removed_notice?: {...} }
//
// See supabase/migrations/20260917000000_custom_links.sql for the admin-side
// removal, which reads these same ids.

// Three, deliberately: the copy at the cap says why, and it is a product
// decision rather than a technical limit.
export const MAX_CUSTOM_LINKS = 3;

export const MAX_LABEL_LENGTH = 30;

// 1MB. These render at 26-38px, so a megabyte is already generous. Note this
// is its own number rather than one inherited from somewhere else: there is no
// single upload pipeline in this app to inherit from — ReaderProfile's avatar
// upload has no size check at all, ProfileBlocks has one site at 3MB and
// another at 5MB, and Community.jsx is at 5MB.
export const MAX_IMAGE_BYTES = 1024 * 1024;

// The bucket every other profile image already uses. A new bucket would need
// its own storage policies, which is a migration and a chance to get RLS wrong
// for no visible gain.
export const LINK_IMAGE_BUCKET = 'avatars';
export const LINK_IMAGE_PREFIX = 'link-icons';

/**
 * Turn what a reader typed into the URL we store, or say why we cannot.
 *
 * Returns a discriminated result rather than a string-or-null, so "this is not
 * a URL" can never be mistaken for "the reader left it blank" by a caller that
 * forgets to check. Same reasoning as the edge-function result shapes.
 *
 *   { ok: true,  url }
 *   { ok: false, reason }   — reason is shown to the reader as-is
 */
export function normalizeLinkUrl(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { ok: false, reason: 'Add a link.' };

  // Bare domains are welcome. A missing scheme is the common case — somebody
  // types mysite.com — so it is upgraded rather than rejected.
  //
  // An explicit http:// is upgraded too. Rejecting it teaches the reader
  // nothing, and storing it puts a mixed-content warning on a page that is
  // otherwise entirely https.
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed.replace(/^http:\/\//i, 'https://')
    : `https://${trimmed}`;

  let parsed;
  try {
    parsed = new URL(withScheme);
  } catch {
    return { ok: false, reason: "That doesn't look like a web address." };
  }

  // A hostname with no dot is not a resolvable address — "mysite" is a typo,
  // and left alone it would render as a link that goes nowhere.
  if (!parsed.hostname.includes('.')) {
    return { ok: false, reason: "That doesn't look like a web address, try mysite.com" };
  }

  // Belt and braces after the upgrade above. new URL() accepts javascript: and
  // data: happily, and either would be an anchor that runs rather than
  // navigates, so anything that is not https by this point is refused outright
  // instead of stored and rendered.
  if (parsed.protocol !== 'https:') {
    return { ok: false, reason: 'Links have to be web addresses.' };
  }

  // Strip credentials rather than keep them. "https://paypal.com@evil.com"
  // goes to evil.com while reading as paypal.com to anyone glancing at it --
  // the oldest link-disguise there is, and these links are shown to strangers
  // on a public profile. Nothing legitimate on a reader's profile needs to
  // carry a username and password in its URL, so dropping them costs nothing
  // and removes the trick. Stripped, not rejected: the address after the @ is
  // the one they typed and is probably what they meant.
  parsed.username = '';
  parsed.password = '';

  return { ok: true, url: parsed.toString() };
}

/**
 * The label as stored: trimmed and capped. The editor also caps the input, so
 * this is the backstop for a paste rather than the only guard.
 */
export function normalizeLinkLabel(raw) {
  return String(raw ?? '').trim().slice(0, MAX_LABEL_LENGTH);
}

/**
 * A link needs an id of its own so a report and an admin removal have
 * something to name. There is no database row here — profile_blocks is a jsonb
 * column on user_profiles, not a table — so nothing assigns one for us.
 *
 * The random suffix is not in block ids, which are `${type}_${Date.now()}`.
 * Two links added in the same millisecond is not a thing a person can do by
 * hand, but a retry or a double-fire can, and a duplicate id here would make
 * an admin removal take the wrong link.
 */
export function newLinkId() {
  return `link_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Read the links out of a block's stored data.
 *
 * Tolerates the three shapes that can actually turn up: a block saved before
 * this feature existed (no data at all), one saved with data but no links key,
 * and a links value that is not an array. Each returns an empty list rather
 * than throwing inside a render.
 */
export function linksFromBlockData(blockData) {
  const links = blockData?.links;
  if (!Array.isArray(links)) return [];
  return links.filter(l => l && typeof l.url === 'string');
}

/**
 * What the report carries as entity_id.
 *
 * The URL alone is not enough: the owner can edit it, and then the moderation
 * queue points at something that no longer exists. Profile, block and link
 * together stay true for as long as the link does, and are exactly what
 * admin_remove_custom_link() takes.
 */
export function customLinkEntityId(profileUserId, blockId, linkId) {
  return `${profileUserId}:${blockId}:${linkId}`;
}

export function parseCustomLinkEntityId(entityId) {
  const parts = String(entityId ?? '').split(':');
  if (parts.length !== 3 || parts.some(p => !p)) return null;
  const [profileUserId, blockId, linkId] = parts;
  return { profileUserId, blockId, linkId };
}
